import { Injectable } from '@angular/core';
import { 
  ContratoSplitCompleto, 
  ColaboradorSplitEntity, 
  DesignacionProductorFonografico,
  TipoDocumentoIdentidad,
  normalizarRolSplitOficial
} from '../models/splits.model';
import { PLANTILLA_SPLIT_SHEET_EPG } from './splits-templates.medellin';

export interface PersonaProductorResumen {
  nombreCompleto: string;
  tipoDocumento: TipoDocumentoIdentidad;
  numeroDocumento: string;
  correo: string;
  origen: 'PARTICIPANTE' | 'ADICIONAL';
}

@Injectable({
  providedIn: 'root'
})
export class SplitsCompilerService {

  /**
   * Compila el Split Sheet oficial en Markdown alineado 1:1 con "Split Sheet.docx"
   */
  compilarContrato(agregado: ContratoSplitCompleto): string {
    const colaboradores = (agregado.colaboradores || []).filter(c => !c.eliminado);
    const prod = this.obtenerProductorNormalizado(agregado);

    const tablaObra = this.generarTablaObraSesionMd(agregado);
    const tablaParticipantes = this.generarTablaParticipantesMd(colaboradores);
    const seleccionLinea = prod.seleccion === 'COPRODUCTORES'
      ? '○ Participante de esta sesión   ● Coproductores (identificar a todos).'
      : '● Participante de esta sesión   ○ Coproductores (identificar a todos).';
    const tablaProductor = this.generarTablaProductorMd(prod, colaboradores);
    const tablaAutorizaciones = this.generarTablaAutorizacionesMd(colaboradores);
    const tablaFirmas = this.generarTablaFirmasMd(colaboradores, prod);

    const prodNombre = this.obtenerNombreProductorDeclarado(prod, colaboradores);
    const prodDoc = this.obtenerDocumentoProductorDeclarado(prod, colaboradores);

    const resultado = PLANTILLA_SPLIT_SHEET_EPG
      .replace(/\{\{tabla_obra_sesion\}\}/g, tablaObra)
      .replace(/\{\{tabla_participantes\}\}/g, tablaParticipantes)
      .replace(/\{\{seleccion_productor_linea\}\}/g, seleccionLinea)
      .replace(/\{\{tabla_productor_fonografico\}\}/g, tablaProductor)
      .replace(/\{\{productor_nombre_declarado\}\}/g, prodNombre)
      .replace(/\{\{productor_documento_declarado\}\}/g, prodDoc)
      .replace(/\{\{tabla_autorizaciones_grabados\}\}/g, tablaAutorizaciones)
      .replace(/\{\{tabla_firmas_electronicas\}\}/g, tablaFirmas);

    return resultado.trim();
  }

  obtenerProductorNormalizado(agregado: ContratoSplitCompleto): DesignacionProductorFonografico {
    const prod = agregado.contrato.productorFonografico;
    const colabs = (agregado.colaboradores || []).filter(c => !c.eliminado);
    const colabMarcado = colabs.find(c => c.esProductorFonografico) || colabs[0];

    if (!prod) {
      return {
        seleccion: 'PARTICIPANTE_SESION',
        modalidad: 'PRODUCTOR_UNICO',
        colaboradorIdDesignado: colabMarcado?.id || '',
        nombreRazonSocial: colabMarcado?.nombreCompleto || '',
        tipoDocumento: colabMarcado?.tipoDocumento || 'CC',
        numeroDocumento: colabMarcado?.numeroDocumento || '',
        correoAceptacion: colabMarcado?.email || '',
        coproductores: []
      };
    }

    if (prod.seleccion === 'PARTICIPANTE_SESION' || prod.modalidad === 'PRODUCTOR_UNICO') {
      const colabDesignado = colabs.find(c => c.id === prod.colaboradorIdDesignado) || colabMarcado;
      if (colabDesignado) {
        return {
          ...prod,
          seleccion: 'PARTICIPANTE_SESION',
          modalidad: 'PRODUCTOR_UNICO',
          colaboradorIdDesignado: colabDesignado.id,
          nombreRazonSocial: colabDesignado.nombreCompleto || prod.nombreRazonSocial || '',
          tipoDocumento: colabDesignado.tipoDocumento || prod.tipoDocumento || 'CC',
          numeroDocumento: colabDesignado.numeroDocumento || prod.numeroDocumento || '',
          correoAceptacion: colabDesignado.email || prod.correoAceptacion || ''
        };
      }
    }

    return prod;
  }

  /**
   * Obtiene la lista completa de coproductores cuando la modalidad es COPRODUCTORES:
   * Incluye los participantes fijos del Paso 2 más los coproductores adicionales agregados en el Paso 3.
   */
  obtenerListaCoproductores(
    prod: DesignacionProductorFonografico,
    colaboradores: ColaboradorSplitEntity[] = []
  ): PersonaProductorResumen[] {
    const lista: PersonaProductorResumen[] = [];

    for (const c of colaboradores.filter(x => !x.eliminado)) {
      lista.push({
        nombreCompleto: c.nombreCompleto?.trim() || 'Participante sin nombre',
        tipoDocumento: c.tipoDocumento || 'CC',
        numeroDocumento: c.numeroDocumento?.trim() || '—',
        correo: c.email?.trim() || '—',
        origen: 'PARTICIPANTE'
      });
    }

    for (const cp of (prod.coproductores || [])) {
      if (cp.colaboradorId && colaboradores.some(c => c.id === cp.colaboradorId)) {
        continue;
      }
      lista.push({
        nombreCompleto: cp.nombreRazonSocial?.trim() || 'Coproductor adicional',
        tipoDocumento: cp.tipoDocumento || 'CC',
        numeroDocumento: cp.numeroDocumento?.trim() || '—',
        correo: cp.correoAceptacion?.trim() || '—',
        origen: 'ADICIONAL'
      });
    }

    return lista;
  }

  obtenerNombreProductorDeclarado(
    prod: DesignacionProductorFonografico,
    colaboradores: ColaboradorSplitEntity[] = []
  ): string {
    if (prod.modalidad === 'COPRODUCTORES') {
      const lista = this.obtenerListaCoproductores(prod, colaboradores);
      const nombres = lista.map(item => item.nombreCompleto).filter(Boolean);
      if (nombres.length > 0) {
        return nombres.join(', ');
      }
    }
    return prod.nombreRazonSocial?.trim() || '________________________________________';
  }

  obtenerDocumentoProductorDeclarado(
    prod: DesignacionProductorFonografico,
    colaboradores: ColaboradorSplitEntity[] = []
  ): string {
    if (prod.modalidad === 'COPRODUCTORES') {
      const lista = this.obtenerListaCoproductores(prod, colaboradores);
      const docs = lista
        .filter(item => item.numeroDocumento && item.numeroDocumento !== '—')
        .map(item => `${item.tipoDocumento || 'CC'} ${item.numeroDocumento}`);
      if (docs.length > 0) {
        return docs.join(', ');
      }
    }
    if (prod.numeroDocumento?.trim()) {
      return `${prod.tipoDocumento || 'CC'} ${prod.numeroDocumento.trim()}`;
    }
    return '__________________________';
  }

  obtenerCorreoProductorDeclarado(
    prod: DesignacionProductorFonografico,
    colaboradores: ColaboradorSplitEntity[] = []
  ): string {
    if (prod.modalidad === 'COPRODUCTORES') {
      const lista = this.obtenerListaCoproductores(prod, colaboradores);
      const correos = lista
        .map(item => item.correo)
        .filter(c => c && c !== '—');
      if (correos.length > 0) {
        return correos.join(', ');
      }
    }
    return prod.correoAceptacion?.trim() || '—';
  }

  private generarTablaObraSesionMd(agregado: ContratoSplitCompleto): string {
    const c = agregado.contrato;
    const codigo = c.codigoSesion || c.id;
    const fechaHora = c.fechaHoraSesion || c.fechaVigencia || new Date().toISOString().slice(0, 16).replace('T', ' ');
    const titulo = c.tituloObra?.trim() || '[Texto]';
    const estudio = c.estudioSede?.trim() || 'Medellín Music Lab';

    let md = '| Campo | Valor | Campo | Valor |\n';
    md += '| :--- | :--- | :--- | :--- |\n';
    md += `| **Código único de sesión** | ${codigo} | **Fecha y hora** | ${fechaHora} |\n`;
    md += `| **Título de la obra** | ${titulo} | **Estudio / sede** | ${estudio} |\n`;
    return md;
  }

  private generarTablaParticipantesMd(colaboradores: ColaboradorSplitEntity[]): string {
    let md = '| Participante / identificación | Rol creativo | IPI (si tiene) | % autoría |\n';
    md += '| :--- | :--- | :---: | :---: |\n';

    if (!colaboradores || colaboradores.length === 0) {
      md += '| [Nombre completo, documento, contacto] | [Rol creativo] | [Opcional] | [0–100] |\n';
      md += '| **TOTAL** | | | **0 %** |\n';
      return md;
    }

    colaboradores.forEach(c => {
      const nombre = c.nombreCompleto?.trim() || 'Por definir';
      const doc = `${c.tipoDocumento || 'CC'} ${c.numeroDocumento?.trim() || 'Pendiente'}`;
      const aka = c.nombreArtistico?.trim() ? ` (${c.nombreArtistico.trim()})` : '';
      const contacto = [c.email?.trim(), c.telefono?.trim()].filter(Boolean).join(' · ');
      const col1 = `${nombre}${aka} — ${doc}${contacto ? ` — ${contacto}` : ''}`;

      const rol = normalizarRolSplitOficial(c.rolContribucion);
      const col2 = rol;

      const col3 = c.ipi?.trim() || '—';
      const col4 = `${Number(c.porcentajeSplit || 0).toFixed(2)} %`;

      md += `| ${col1} | ${col2} | ${col3} | **${col4}** |\n`;
    });

    const suma = colaboradores.reduce((acc, c) => acc + (Number(c.porcentajeSplit) || 0), 0);
    md += `| **TOTAL** | | | **${suma.toFixed(2)} %** |\n`;
    return md;
  }

  private generarTablaProductorMd(
    prod: DesignacionProductorFonografico,
    colaboradores: ColaboradorSplitEntity[]
  ): string {
    const modalidadStr = prod.modalidad === 'COPRODUCTORES'
      ? '○ Productor único    ● Coproductores (identificar a todos y documentar su participación)'
      : '● Productor único    ○ Coproductores (identificar a todos y documentar su participación)';

    const nombre = this.obtenerNombreProductorDeclarado(prod, colaboradores);
    const doc = this.obtenerDocumentoProductorDeclarado(prod, colaboradores);
    const correo = this.obtenerCorreoProductorDeclarado(prod, colaboradores);

    let md = '| Concepto | Detalle |\n';
    md += '| :--- | :--- |\n';
    md += `| **Nombre completo** | ${nombre} |\n`;
    md += `| **Tipo y número de identificación** | ${doc} |\n`;
    md += `| **Correo para aceptación** | ${correo} |\n`;
    md += `| **Modalidad** | ${modalidadStr} |\n`;
    if (prod.modalidad === 'COPRODUCTORES') {
      const lista = this.obtenerListaCoproductores(prod, colaboradores);
      if (lista.length > 0) {
        const detalle = lista
          .map(item => `${item.nombreCompleto} (${item.tipoDocumento} ${item.numeroDocumento} · ${item.correo})`)
          .join(' | ');
        md += `| **Coproductores registrados** | ${detalle} |\n`;
      }
    }
    return md;
  }

  private generarTablaAutorizacionesMd(colaboradores: ColaboradorSplitEntity[]): string {
    let md = '| Participante | ¿Autoriza la fijación, edición, mezcla, masterización y archivo técnico de su interpretación o ejecución, así como su reproducción, distribución, comunicación pública y puesta a disposición incorporada en el fonograma? |\n';
    md += '| :--- | :---: |\n';

    if (!colaboradores || colaboradores.length === 0) {
      md += '| [Nombre autocompletado] | ○ Sí   ○ No |\n';
      return md;
    }

    colaboradores.forEach(c => {
      const nombre = c.nombreCompleto?.trim() || 'Participante sin nombre';
      const autStr = c.autorizaFijacion === 'NO'
        ? '○ Sí   ● No'
        : '● Sí   ○ No';

      md += `| ${nombre} | ${autStr} |\n`;
    });

    return md;
  }

  private generarTablaFirmasMd(
    colaboradores: ColaboradorSplitEntity[],
    prod: DesignacionProductorFonografico
  ): string {
    let md = '| Firmante / calidad | Aceptación y firma | Fecha y trazabilidad |\n';
    md += '| :--- | :--- | :--- |\n';

    colaboradores.forEach((c, idx) => {
      const rol = normalizarRolSplitOficial(c.rolContribucion);
      const calidad = `Participante ${idx + 1}: ${c.nombreCompleto || 'Por definir'} (${c.tipoDocumento} ${c.numeroDocumento || '—'} · ${rol} · ${Number(c.porcentajeSplit || 0).toFixed(2)}%)`;
      const f = c.firma;
      const aceptacion = f?.firmado
        ? `☑ Aceptación expresa · Firma: ${f.firmaTexto || c.nombreCompleto}`
        : '☐ Pendiente de aceptación y firma electrónica';
      const traza = f?.firmado
        ? `${f.fechaFirma ? new Date(f.fechaFirma).toLocaleString('es-CO') : 'Firmado'} · ${f.hashVerificacion || 'Sello digital MML'}`
        : 'Pendiente de firma';
      md += `| ${calidad} | ${aceptacion} | ${traza} |\n`;
    });

    const prodNombre = this.obtenerNombreProductorDeclarado(prod, colaboradores);
    const prodDoc = this.obtenerDocumentoProductorDeclarado(prod, colaboradores);
    const fProd = prod.firmaProductor;
    const prodCalidad = `Productor fonográfico designado: ${prodNombre} (${prodDoc})`;
    const prodAceptacion = fProd?.firmado
      ? `☑ Aceptación expresa · Firma: ${fProd.firmaTexto || prodNombre}`
      : '☐ Pendiente de aceptación expresa y firma';
    const prodTraza = fProd?.firmado
      ? `${fProd.fechaFirma ? new Date(fProd.fechaFirma).toLocaleString('es-CO') : 'Firmado'} · ${fProd.hashVerificacion || 'Sello digital MML'}`
      : 'Pendiente de firma';

    md += `| ${prodCalidad} | ${prodAceptacion} | ${prodTraza} |\n`;

    return md;
  }

  /**
   * Genera un documento HTML 100% autocontenido, idéntico visual y estructuralmente a "Split Sheet.docx"
   */
  compilarDocumentoHtml(agregado: ContratoSplitCompleto): string {
    const c = agregado.contrato;
    const colaboradores = (agregado.colaboradores || []).filter(col => !col.eliminado);
    const prod = this.obtenerProductorNormalizado(agregado);

    const codigoSesion = c.codigoSesion || c.id;
    const fechaHora = c.fechaHoraSesion || c.fechaVigencia || new Date().toISOString().slice(0, 16).replace('T', ' ');
    const tituloObra = c.tituloObra?.trim() || '—';
    const estudioSede = c.estudioSede?.trim() || 'Medellín Music Lab';

    const sumaTotal = colaboradores.reduce((acc, col) => acc + (Number(col.porcentajeSplit) || 0), 0);

    const filasParticipantesHtml = colaboradores.map(col => {
      const rol = normalizarRolSplitOficial(col.rolContribucion);
      const aka = col.nombreArtistico?.trim() ? ` (${col.nombreArtistico.trim()})` : '';
      const contacto = [col.email?.trim(), col.telefono?.trim()].filter(Boolean).join(' · ');
      return `
        <tr>
          <td>
            <strong>${col.nombreCompleto || 'Por definir'}${aka}</strong><br>
            <span style="font-size: 8.5pt; color: #334155;">${col.tipoDocumento || 'CC'} ${col.numeroDocumento || 'Pendiente'}${contacto ? ' · ' + contacto : ''}</span>
          </td>
          <td><strong>${rol}</strong></td>
          <td style="text-align: center;">${col.ipi?.trim() || '—'}</td>
          <td style="text-align: right; font-weight: bold;">${Number(col.porcentajeSplit || 0).toFixed(2)} %</td>
        </tr>`;
    }).join('');

    const seleccionLineaHtml = prod.seleccion === 'COPRODUCTORES'
      ? '○ Participante de esta sesión &nbsp;&nbsp; <strong>● Coproductores (identificar a todos).</strong>'
      : '<strong>● Participante de esta sesión</strong> &nbsp;&nbsp; ○ Coproductores (identificar a todos).';

    const modalidadProdHtml = prod.modalidad === 'COPRODUCTORES'
      ? '○ Productor único &nbsp;&nbsp;&nbsp; <strong>● Coproductores (identificar a todos y documentar su participación)</strong>'
      : '<strong>● Productor único</strong> &nbsp;&nbsp;&nbsp; ○ Coproductores (identificar a todos y documentar su participación)';

    const prodNombre = this.obtenerNombreProductorDeclarado(prod, colaboradores);
    const prodDoc = this.obtenerDocumentoProductorDeclarado(prod, colaboradores);
    const prodCorreo = this.obtenerCorreoProductorDeclarado(prod, colaboradores);

    const filasAutorizacionesHtml = colaboradores.map(col => {
      const autHtml = col.autorizaFijacion === 'NO'
        ? '○ Sí &nbsp;&nbsp; <strong>● No</strong>'
        : '<strong>● Sí</strong> &nbsp;&nbsp; ○ No';

      return `
        <tr>
          <td><strong>${col.nombreCompleto || 'Participante'}</strong></td>
          <td style="text-align: center;">${autHtml}</td>
        </tr>`;
    }).join('');

    const renderFirmaCelda = (f?: any, nombreDefault?: string) => {
      if (!f || !f.firmado) {
        return `<span style="color: #64748b;">☐ Pendiente de aceptación y firma</span>`;
      }
      const imgHtml = f.firmaDataUrl
        ? `<div style="margin: 4px 0;"><img src="${f.firmaDataUrl}" alt="Firma" style="max-height: 42px; max-width: 180px; object-fit: contain;" /></div>`
        : `<div style="margin: 4px 0; font-family: 'Brush Script MT', cursive, serif; font-size: 13pt; color: #0f172a;">${f.firmaTexto || nombreDefault || 'Firmado'}</div>`;
      return `
        <div>
          <strong style="color: #166534;">☑ Aceptación expresa confirmada</strong>
          ${imgHtml}
        </div>`;
    };

    const renderTrazaCelda = (f?: any) => {
      if (!f || !f.firmado) {
        return `<span style="color: #94a3b8;">Sin sello de tiempo</span>`;
      }
      const fechaStr = f.fechaFirma ? new Date(f.fechaFirma).toLocaleString('es-CO') : '';
      return `
        <div style="font-size: 8pt; color: #1e293b;">
          <div><strong>Fecha:</strong> ${fechaStr}</div>
          <div><strong>Sello:</strong> ${f.hashVerificacion || 'MML-VERIFIED'}</div>
          <div>${f.trazabilidadTexto || 'Firma electrónica directa en Medellín Music Lab'}</div>
        </div>`;
    };

    const filasFirmasHtml = colaboradores.map((col, idx) => {
      const rol = normalizarRolSplitOficial(col.rolContribucion);
      return `
        <tr>
          <td>
            <strong>Participante ${idx + 1}: ${col.nombreCompleto || 'Por definir'}</strong><br>
            <span style="font-size: 8pt; color: #475569;">${col.tipoDocumento || 'CC'} ${col.numeroDocumento || '—'} · ${rol} · ${Number(col.porcentajeSplit || 0).toFixed(2)} %</span>
          </td>
          <td>${renderFirmaCelda(col.firma, col.nombreCompleto)}</td>
          <td>${renderTrazaCelda(col.firma)}</td>
        </tr>`;
    }).join('');

    const filaFirmaProductorHtml = `
      <tr>
        <td>
          <strong>Productor fonográfico designado:</strong><br>
          <span>${prodNombre}</span><br>
          <span style="font-size: 8pt; color: #475569;">${prodDoc}</span>
        </td>
        <td>${renderFirmaCelda(prod.firmaProductor, prodNombre)}</td>
        <td>${renderTrazaCelda(prod.firmaProductor)}</td>
      </tr>`;

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>SPLIT SHEET | ${tituloObra}</title>
  <style>
    @page {
      size: letter;
      margin: 15mm 15mm 18mm 15mm;
    }
    * {
      box-sizing: border-box;
    }
    body {
      font-family: Verdana, Geneva, Tahoma, sans-serif;
      font-size: 9.5pt;
      line-height: 1.45;
      color: #111827;
      background-color: #f1f5f9;
      margin: 0;
      padding: 24px 12px;
    }
    .acciones-barra {
      max-width: 840px;
      margin: 0 auto 16px auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #0f172a;
      color: #fff;
      padding: 10px 18px;
      border-radius: 8px;
    }
    .acciones-barra button {
      background: #0F6B55;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
    }
    .documento-hoja {
      max-width: 840px;
      margin: 0 auto;
      background: #ffffff;
      padding: 40px 48px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08);
    }
    .titulo-principal {
      font-size: 14pt;
      font-weight: bold;
      margin: 0 0 4px 0;
      color: #000000;
    }
    .subtitulo-principal {
      font-size: 9.5pt;
      font-weight: bold;
      margin: 0 0 3px 0;
      color: #111827;
    }
    .linea-programa {
      font-size: 9pt;
      margin: 0 0 16px 0;
      color: #1f2937;
    }
    h2.seccion-heading {
      font-size: 10.5pt;
      font-weight: bold;
      margin: 18px 0 6px 0;
      color: #000000;
    }
    p.parrafo-doc {
      margin: 6px 0;
      text-align: left;
      font-size: 9pt;
    }
    table.tabla-cuadricula {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0 10px 0;
      font-size: 9pt;
    }
    table.tabla-cuadricula th,
    table.tabla-cuadricula td {
      border: 1px solid #000000;
      padding: 6px 8px;
      vertical-align: top;
      text-align: left;
    }
    table.tabla-cuadricula th {
      background-color: #f8fafc;
      font-weight: bold;
    }
    .footer-oficial {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #cbd5e1;
      text-align: center;
      font-size: 8.5pt;
      color: #334155;
    }
    @media print {
      body {
        background: transparent !important;
        padding: 0 !important;
      }
      .acciones-barra {
        display: none !important;
      }
      .documento-hoja {
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        max-width: 100% !important;
      }
    }
  </style>
</head>
<body>
  <div class="acciones-barra">
    <div>
      <strong>SPLIT SHEET | ACUERDO DE PARTICIPACIONES</strong><br>
      <span style="font-size: 11px; color: #cbd5e1;">MEDELLÍN MUSIC LAB</span>
    </div>
    <button onclick="window.print()">Imprimir / Guardar PDF</button>
  </div>

  <div class="documento-hoja">
    <div class="titulo-principal">SPLIT SHEET | ACUERDO DE PARTICIPACIONES</div>
    <div class="subtitulo-principal">Obra musical · autorización de fijación · identificación del productor fonográfico</div>
    <div class="linea-programa">MEDELLÍN MUSIC LAB</div>

    <h2 class="seccion-heading">1. Obra y sesión</h2>
    <table class="tabla-cuadricula">
      <tbody>
        <tr>
          <td style="width: 22%;"><strong>Código único de sesión</strong></td>
          <td style="width: 28%;">${codigoSesion}</td>
          <td style="width: 22%;"><strong>Fecha y hora</strong></td>
          <td style="width: 28%;">${fechaHora}</td>
        </tr>
        <tr>
          <td><strong>Título de la obra</strong></td>
          <td><strong>${tituloObra}</strong></td>
          <td><strong>Estudio / sede</strong></td>
          <td>${estudioSede}</td>
        </tr>
      </tbody>
    </table>

    <h2 class="seccion-heading">2. Participantes y reparto de la obra musical</h2>
    <p class="parrafo-doc">
      El porcentaje corresponde únicamente a la participación convenida en los derechos patrimoniales de autor de la COMPOSICIÓN, no al fonograma, a la interpretación ni a los ingresos del máster.
    </p>
    <table class="tabla-cuadricula">
      <thead>
        <tr>
          <th style="width: 36%;">Participante / identificación</th>
          <th style="width: 33%;">Rol creativo</th>
          <th style="width: 13%; text-align: center;">IPI (si tiene)</th>
          <th style="width: 18%; text-align: right;">% autoría</th>
        </tr>
      </thead>
      <tbody>
        ${filasParticipantesHtml}
        <tr>
          <td><strong>TOTAL</strong></td>
          <td></td>
          <td></td>
          <td style="text-align: right;"><strong>${sumaTotal.toFixed(2)} %</strong></td>
        </tr>
      </tbody>
    </table>
    <p class="parrafo-doc">
      Las partes acuerdan que las participaciones indicadas representan el 100 % de la titularidad patrimonial de autor que reconocen entre los firmantes sobre esta obra. Dichas participaciones son convenidas libremente por los firmantes y pueden reconocerse a cualquier participante, incluido el productor musical, con independencia de su rol en la sesión. Los derechos morales permanecen en cabeza de sus autores conforme a la ley aplicable.
    </p>

    <h2 class="seccion-heading">3. Designación del productor fonográfico</h2>
    <p class="parrafo-doc">
      <strong>Selección obligatoria:</strong> &nbsp; ${seleccionLineaHtml}
    </p>
    <table class="tabla-cuadricula">
      <tbody>
        <tr>
          <td style="width: 36%;"><strong>Nombre completo</strong></td>
          <td style="width: 64%;">${prodNombre}</td>
        </tr>
        <tr>
          <td><strong>Tipo y número de identificación</strong></td>
          <td>${prodDoc}</td>
        </tr>
        <tr>
          <td><strong>Correo para aceptación</strong></td>
          <td>${prodCorreo}</td>
        </tr>
        <tr>
          <td><strong>Modalidad</strong></td>
          <td>${modalidadProdHtml}</td>
        </tr>
      </tbody>
    </table>
    <p class="parrafo-doc">
      Los participantes que suscriben el presente Split Sheet, de manera libre, expresa e informada, aceptan y reconocen que <strong>${prodNombre}</strong>, identificado(a) con <strong>${prodDoc}</strong>, es el (la) PRODUCTOR(A) FONOGRÁFICO(A) del fonograma resultante de esta sesión de grabación y, en consecuencia, titular exclusivo(a) de los derechos patrimoniales sobre dicho fonograma (MÁSTER). En esa condición podrá reproducirlo, distribuirlo, comercializarlo, comunicarlo públicamente, ponerlo a disposición del público, licenciarlo y explotarlo económicamente, directamente o por terceros, en cualquier territorio y por cualquier medio, durante el término de protección legal.
    </p>

    <h2 class="seccion-heading">4. Autorizaciones de los participantes grabados</h2>
    <p class="parrafo-doc">
      Cada persona con voz o interpretación/ejecución incorporada al audio responde individualmente:
    </p>
    <table class="tabla-cuadricula">
      <thead>
        <tr>
          <th style="width: 38%;">Participante</th>
          <th style="width: 62%; text-align: center;">¿Autoriza la fijación, edición, mezcla, masterización y archivo técnico de su interpretación o ejecución, así como su reproducción, distribución, comunicación pública y puesta a disposición incorporada en el fonograma?</th>
        </tr>
      </thead>
      <tbody>
        ${filasAutorizacionesHtml}
      </tbody>
    </table>
    <p class="parrafo-doc">
      Cada participante cuya voz, interpretación o ejecución haya quedado incorporada en la grabación debe responder individualmente la autorización anterior. La respuesta afirmativa autoriza al (la) productor(a) fonográfico(a) a explotar dicha interpretación o ejecución en los términos de la sección 3. Si alguna persona marca «No», su intervención no podrá utilizarse mientras no exista autorización válida o se retire del fonograma.
    </p>

    <h2 class="seccion-heading">5. Aceptación y firma electrónica</h2>
    <p class="parrafo-doc">
      Cada firmante confirma sus datos, aporte y porcentaje de derechos patrimoniales de autor cuando corresponda; acepta expresamente la designación del productor fonográfico identificado en la sección 3 y las autorizaciones individuales que haya otorgado. Las partes manifiestan que han leído y aceptan libremente este documento. El productor fonográfico designado debe firmar para dejar constancia de su aceptación y de la condición declarada. Con la firma del presente Split Sheet, cada firmante declara que su aporte es original y no infringe derechos de terceros.
    </p>
    <table class="tabla-cuadricula">
      <thead>
        <tr>
          <th style="width: 35%;">Firmante / calidad</th>
          <th style="width: 34%;">Aceptación y firma</th>
          <th style="width: 31%;">Fecha y trazabilidad</th>
        </tr>
      </thead>
      <tbody>
        ${filasFirmasHtml}
        ${filaFirmaProductorHtml}
      </tbody>
    </table>
    <p class="parrafo-doc">
      Medellín Music Lab facilita el diligenciamiento y conserva el registro; no adquiere derechos por esa sola intervención. El monitor solo verifica que el flujo de diligenciamiento se haya completado, sin certificar titularidades ni asesorar individualmente a las partes.
    </p>

    <div class="footer-oficial">
      Medellín Music Lab &nbsp;|&nbsp; Split Sheet digital
    </div>
  </div>
</body>
</html>`;
  }
}
